/** Takt-train HANDLERS for the pull-plan grid. The protoTrain / trainReview
 *  useState live in the component (they ride the undo snapshot + autosave
 *  payload); this hook receives them plus the board mutators, dep-graph
 *  handlers, members and pushUndo, and returns the create / apply / commit /
 *  tie / car / level handlers and the late train-derived memos. */
import React from "react";
import type {
  DepGraph,
  DraftPayload,
  ScopeMember,
  TrainDepType,
  TrainState,
} from "../../../../models";
import {
  addTrainDep,
  mappedRefs,
  removeTrainCar,
  removeTrainDep,
  simulateTrain,
  trainWouldCycle,
  updateTrainCarDuration,
  updateTrainDep,
} from "../../../../models";
import { fetchDepEditorGraph } from "../../../../api/clientV2";
import { notify, confirmNotice } from "../../../../components/Notice/noticeStore";
import {
  buildTrainFromSelection,
  findTrainInLeaf,
  type SelectionCar,
  type SelectionTie,
} from "../train/trainReconcile";
import { buildTrainGraph } from "../train/trainGraph";
import type { TrainApplyContext } from "../train/trainApply";
import {
  buildTrainApplyReview,
  reviewToPlan,
  rebaseTrainBaselines,
} from "../train/trainApplyReview";
import type { TrainApplyReview } from "../train/trainApplyReview";
import { makeId } from "../../../../utils/id";
import { addEdge, removeEdge } from "../../depCore/edgeIntents";
import { addNewActivity, stageDurationEdit } from "../../draft/draftOps";
import type { BarSpan } from "../../WbsBoardView/WbsBoardView";
import type { ProtoNewAct } from "../PullPlanGrid";

interface TrainLevelChip {
  index: number;
  label: string;
  isSource: boolean;
}

interface DayPos {
  startDay: number;
  finishDay: number;
}

interface TrainGraphBundle {
  graph: DepGraph;
  boardDates: Map<string, { start: string | null; finish: string | null }>;
}

interface Args {
  protoTrain: TrainState | null;
  setProtoTrain: React.Dispatch<React.SetStateAction<TrainState | null>>;
  trainSeqMode: "board" | "live";
  setTrainTiesView: React.Dispatch<React.SetStateAction<boolean>>;
  setFocusedLeaf: React.Dispatch<React.SetStateAction<string | null>>;
  setProtoSelection: React.Dispatch<React.SetStateAction<Set<number>>>;
  // Board mutators (from useBoardMutators) + dep-graph apply flag.
  protoSpanOf: (activityId: number) => BarSpan | null;
  deleteTrainMappedRefs: (refs: string[]) => void;
  pendingApplyDatesRef: React.MutableRefObject<boolean>;
  setProtoNewActs: React.Dispatch<React.SetStateAction<ProtoNewAct[]>>;
  setProtoSpans: React.Dispatch<React.SetStateAction<Map<number, BarSpan>>>;
  setPendingOps: React.Dispatch<React.SetStateAction<DraftPayload>>;
  depGraphRef: React.MutableRefObject<DepGraph | null>;
  // Selection / board derivations.
  orderedSelection: () => SelectionCar[];
  boardLeafCars: Map<string, SelectionCar[]>;
  allScopeLeaves: Array<{ key: string; label: string }>;
  // Members + lookups.
  scopeMembers: ScopeMember[] | null;
  protoNewActs: ProtoNewAct[];
  codeNameByKey: Record<string, string>;
  effectiveCodeColors: Record<string, string>;
  protoCalendar: string[];
  // Section context for on-demand graph fetch.
  scheduleId: number;
  depEditorSection?: string;
  effectiveDraft: DraftPayload;
  pushUndo: (label?: string) => void;
}

export interface TrainHandlers {
  handleCreateTrain: () => Promise<void>;
  handleClearTrain: () => void;
  trainLevelChips: TrainLevelChip[];
  handleRemoveTrainLevel: (index: number) => void;
  handleAddTrainLevel: () => void;
  trainCanAddLevel: boolean;
  handleApplyTrain: () => Promise<boolean>;
  handleAddTrainDep: (
    predCarId: string,
    succCarId: string,
    type: TrainDepType,
    lagDays: number,
  ) => void;
  handleUpdateTrainDep: (
    predCarId: string,
    succCarId: string,
    type: TrainDepType,
    lagDays: number,
  ) => void;
  handleRemoveTrainDep: (predCarId: string, succCarId: string) => void;
  handleUpdateTrainCarDuration: (carId: string, durationDays: number) => void;
  handleRemoveTrainCars: (carIds: string[]) => void;
  trainGraphBundle: TrainGraphBundle | null;
  trainMappedRefs: Set<number>;
}

export function useTrainHandlers({
  protoTrain,
  setProtoTrain,
  trainSeqMode,
  setTrainTiesView,
  setFocusedLeaf,
  setProtoSelection,
  protoSpanOf,
  deleteTrainMappedRefs,
  pendingApplyDatesRef,
  setProtoNewActs,
  setProtoSpans,
  setPendingOps,
  depGraphRef,
  orderedSelection,
  boardLeafCars,
  allScopeLeaves,
  scopeMembers,
  protoNewActs,
  codeNameByKey,
  effectiveCodeColors,
  protoCalendar,
  scheduleId,
  depEditorSection,
  effectiveDraft,
  pushUndo,
}: Args): TrainHandlers {
  // Committed ties whose BOTH endpoints are in `refs` (the selection's internal
  // sequence) — these become the train's element ties.
  const internalTiesAmong = React.useCallback(
    (refs: Set<string>, graph: DepGraph): SelectionTie[] => {
      const ties: SelectionTie[] = [];
      for (const [succId, edges] of Object.entries(graph.edges)) {
        if (!refs.has(succId)) continue;
        for (const e of edges) {
          if (!refs.has(e.pred_id)) continue;
          ties.push({
            predRef: e.pred_id,
            succRef: succId,
            type: e.type,
            lagDays: e.lag_hours / 8,
          });
        }
      }
      return ties;
    },
    [],
  );

  // Create a train from the selection: one element per activity (board order),
  // its internal ties, and the selection recorded as instance #0.
  const handleCreateTrain = React.useCallback(async () => {
    const items = orderedSelection();
    if (items.length === 0) {
      notify("Pick activities to build a train from.");
      return;
    }
    if (items.some((it) => !(it.code || "").trim())) {
      notify(
        "Uncoded activities can't be part of a train and were skipped — a train maps by work code.",
      );
    }
    if (protoTrain && protoTrain.cars.length > 0) {
      const ok = await confirmNotice(
        "A train already exists. Replacing it discards it and its mappings. Replace it?",
      );
      if (!ok) return;
    }
    let g = depGraphRef.current;
    if (!g && depEditorSection) {
      try {
        g = await fetchDepEditorGraph(scheduleId, depEditorSection, effectiveDraft);
      } catch {
        g = null;
      }
    }
    const refs = new Set(items.map((it) => it.ref));
    const ties = g ? internalTiesAmong(refs, g) : [];
    const train = buildTrainFromSelection(items, ties);
    // Auto-map the run onto every OTHER level it repeats on, so picking the
    // subset once is enough — no re-selecting it level by level.
    const instances = [...train.instances];
    boardLeafCars.forEach((cars) => {
      if (cars.some((c) => refs.has(c.ref))) return; // the source level (instance #0)
      const inst = findTrainInLeaf(train, cars);
      if (inst) instances.push(inst);
    });
    pushUndo("Create train");
    setProtoTrain({ ...train, instances });
    setProtoSelection(new Set());
    setTrainTiesView(true);
    // setState setters + depGraphRef ref are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orderedSelection,
    internalTiesAmong,
    protoTrain,
    depEditorSection,
    scheduleId,
    effectiveDraft,
    boardLeafCars,
    pushUndo,
  ]);

  const handleClearTrain = React.useCallback(() => {
    pushUndo("Clear train");
    setProtoTrain(null);
    // Drop the train/level focus so the gantt resets to the whole board.
    setTrainTiesView(false);
    setFocusedLeaf(null);
    // setState setters are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pushUndo]);

  // The levels the train currently applies to — one chip per mapped instance,
  // labelled by its WBS leaf. Instance #0 is the source level (not removable).
  const trainLevelChips = React.useMemo(() => {
    if (!protoTrain || protoTrain.instances.length === 0)
      return [] as TrainLevelChip[];
    const refToLeaf = new Map<string, string>();
    boardLeafCars.forEach((cars, leafKey) =>
      cars.forEach((c) => refToLeaf.set(c.ref, leafKey)),
    );
    const labelOf = new Map(allScopeLeaves.map((l) => [l.key, l.label]));
    return protoTrain.instances.map((inst, i) => {
      const ref = Object.values(inst)[0];
      const leafKey = ref ? refToLeaf.get(ref) ?? null : null;
      return {
        index: i,
        isSource: i === 0,
        label: leafKey ? labelOf.get(leafKey) ?? leafKey : `Level ${i + 1}`,
      };
    });
  }, [protoTrain, boardLeafCars, allScopeLeaves]);

  // Drop one mapped level from the train (the source level stays).
  const handleRemoveTrainLevel = React.useCallback(
    (index: number) => {
      if (index === 0) return;
      pushUndo("Drop train level");
      setProtoTrain((t) =>
        t ? { ...t, instances: t.instances.filter((_, i) => i !== index) } : t,
      );
    },
    // setProtoTrain setter is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pushUndo],
  );

  // Levels the train ISN'T mapped to yet but whose work the run fits — the
  // candidates "+ Level" can add. Maps an instance back to its leaf the same
  // way trainLevelChips does (first ref → leaf).
  const unmappedTrainLeaves = React.useMemo(() => {
    // A train with no elements matches every leaf vacuously — never offer "+ Level"
    // for it (it would add an arbitrary, empty instance). Levels are addable only
    // once the run has work that another level actually carries.
    if (!protoTrain || protoTrain.cars.length === 0)
      return [] as Array<{ leafKey: string; cars: SelectionCar[] }>;
    const refToLeaf = new Map<string, string>();
    boardLeafCars.forEach((cars, leafKey) =>
      cars.forEach((c) => refToLeaf.set(String(c.ref), leafKey)),
    );
    const mapped = new Set<string>();
    protoTrain.instances.forEach((inst) => {
      const ref = Object.values(inst)[0];
      const lk = ref != null ? refToLeaf.get(String(ref)) : undefined;
      if (lk) mapped.add(lk);
    });
    const out: Array<{ leafKey: string; cars: SelectionCar[] }> = [];
    boardLeafCars.forEach((cars, leafKey) => {
      if (mapped.has(leafKey)) return;
      if (findTrainInLeaf(protoTrain, cars)) out.push({ leafKey, cars });
    });
    return out;
  }, [protoTrain, boardLeafCars]);
  const trainCanAddLevel = unmappedTrainLeaves.length > 0;

  // Add the next matching level to the train (the inverse of dropping one).
  const handleAddTrainLevel = React.useCallback(() => {
    const next = unmappedTrainLeaves[0];
    if (!protoTrain || !next) return;
    const inst = findTrainInLeaf(protoTrain, next.cars);
    if (!inst) return;
    pushUndo("Add train level");
    setProtoTrain((t) => (t ? { ...t, instances: [...t.instances, inst] } : t));
    // setProtoTrain setter is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protoTrain, unmappedTrainLeaves, pushUndo]);

  // Apply a built review straight to the board (no preview): stage creates /
  // duration syncs / tie edits for every (level, element). Positions + dates
  // come from the forward pass — never the train — and locked work is untouched.
  // Folding created refs into the instances keeps a re-Apply a stable no-op.
  const applyTrainPlan = React.useCallback(
    (review: TrainApplyReview, graph: DepGraph, excluded: Set<string>) => {
      if (!protoTrain) return;
      const plan = reviewToPlan(review, excluded);

      pushUndo("Apply train");
      // Reflect the apply on the board: once the graph refetches with these
      // edits, snap the committed bars onto the new dates.
      pendingApplyDatesRef.current = true;
      const pendingDeletes = protoTrain.pendingRowDeletes ?? [];
      if (pendingDeletes.length > 0) deleteTrainMappedRefs(pendingDeletes);
      if (plan.creates.length > 0) {
        setProtoNewActs((prev) => [
          ...prev,
          ...plan.creates.map((c) => ({
            draftId: c.draftId,
            wbsKey: c.wbsKey,
            wbsPath: c.wbsPath,
            wbsNodeId: c.wbsNodeId,
            startIndex: c.startIndex,
            durationDays: c.durationDays,
            code: c.code,
            name: c.name,
          })),
        ]);
      }
      if (plan.durDraft.length > 0) {
        setProtoNewActs((prev) =>
          prev.map((n) => {
            const d = plan.durDraft.find((x) => x.draftId === n.draftId);
            return d ? { ...n, durationDays: d.days } : n;
          }),
        );
      }
      if (plan.durCommitted.length > 0) {
        setProtoSpans((prev) => {
          const next = new Map(prev);
          for (const { id, days } of plan.durCommitted) {
            const sp = next.get(id) ?? protoSpanOf(id);
            if (!sp) continue;
            next.set(id, { startIndex: sp.startIndex, durationDays: days });
          }
          return next;
        });
      }
      setPendingOps((prev) => {
        let next = prev;
        for (const c of plan.creates) {
          next = addNewActivity(next, {
            draft_id: c.draftId,
            name: c.name,
            takt_a: null,
            takt_b: null,
            takt_c: null,
            work_code: c.code,
            duration_days: c.durationDays,
          });
        }
        for (const { id, days } of plan.durCommitted) next = stageDurationEdit(next, id, days);
        for (const { draftId, days } of plan.durDraft) {
          next = {
            ...next,
            pending_new_activities: next.pending_new_activities.map((a) =>
              a.draft_id === draftId ? { ...a, duration_days: days } : a,
            ),
          };
        }
        let edits = next.pending_dep_edits;
        for (const r of plan.tieRemovals) edits = removeEdge(edits, graph, r.succ, r.pred);
        for (const a of plan.tieAdds) edits = addEdge(edits, graph, a.pred, a.succ, a.type, a.lag);
        return { ...next, pending_dep_edits: edits };
      });
      // Rebase the template's baselines to what was just applied so the NEXT
      // Apply diffs against the board's new state — a re-Apply with no further
      // edits is a strict no-op.
      const rebased = rebaseTrainBaselines(protoTrain, review, excluded);
      setProtoTrain((t) =>
        t
          ? {
              ...t,
              instances: plan.nextInstances,
              pendingRowDeletes: [],
              cars: rebased.cars,
              baselineDeps: rebased.baselineDeps,
            }
          : t,
      );
    },
    // setState setters + pendingApplyDatesRef ref are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [protoTrain, pushUndo, deleteTrainMappedRefs, protoSpanOf],
  );

  // Apply: sync every mapped instance to the template. For each instance, set
  // each mapped activity's duration to its element's, mirror the train's
  // internal ties, CREATE a row activity for any element a chunk is missing, and
  // flush row activities orphaned by deleted elements (pendingRowDeletes). This
  // is the one place the template propagates to the rows. Cross-instance /
  // external ties are never touched.
  const handleApplyTrain = React.useCallback(async () => {
    if (
      !protoTrain ||
      protoTrain.cars.length === 0 ||
      protoTrain.instances.length === 0
    ) {
      notify("Map at least one set of activities to the train first.");
      return false;
    }
    let g = depGraphRef.current;
    if (!g && depEditorSection) {
      try {
        g = await fetchDepEditorGraph(scheduleId, depEditorSection, effectiveDraft);
      } catch {
        g = null;
      }
    }
    if (!g) {
      notify("Couldn't load this section's dependencies yet — try again in a moment.", "error");
      return false;
    }
    const graph = g;
    // Build the reconcile and apply it straight to the board — no preview.
    const ctx: TrainApplyContext = {
      leafOf: (ref) => {
        if (/^\d+$/.test(ref)) {
          const m = (scopeMembers ?? []).find((x) => x.activity_id === Number(ref));
          if (!m) return null;
          const wp = m.wbs_path.length > 0 ? m.wbs_path : ["(no WBS node)"];
          return { wbsKey: wp.join(" "), wbsPath: wp, wbsNodeId: m.wbs_node_id };
        }
        const n = protoNewActs.find((x) => x.draftId === ref);
        return n ? { wbsKey: n.wbsKey, wbsPath: n.wbsPath, wbsNodeId: n.wbsNodeId } : null;
      },
      startOf: (ref) => {
        if (/^\d+$/.test(ref)) {
          const sp = protoSpanOf(Number(ref));
          return sp ? sp.startIndex : null;
        }
        const n = protoNewActs.find((x) => x.draftId === ref);
        return n ? n.startIndex : null;
      },
      isCommitted: (ref) => /^\d+$/.test(ref),
      isLocked: (ref) => {
        if (!/^\d+$/.test(ref)) return false; // new draft activities are never locked
        const m = (scopeMembers ?? []).find((x) => x.activity_id === Number(ref));
        return !!m && (m.status === "TK_Complete" || m.status === "TK_Active");
      },
      durationOf: (ref) => {
        if (/^\d+$/.test(ref)) return protoSpanOf(Number(ref))?.durationDays ?? null;
        const n = protoNewActs.find((x) => x.draftId === ref);
        return n ? n.durationDays : null;
      },
      hasEdge: (pred, succ) => (graph.edges[succ] ?? []).some((e) => e.pred_id === pred),
      edgeType: (pred, succ) => {
        const e = (graph.edges[succ] ?? []).find((x) => x.pred_id === pred);
        return e ? { type: e.type, lag: Math.round(e.lag_hours / 8) } : null;
      },
      edgesAmong: (refs) => {
        const set = new Set(refs);
        const out: Array<{ pred: string; succ: string }> = [];
        for (const [succId, edges] of Object.entries(graph.edges)) {
          if (!set.has(succId)) continue;
          for (const e of edges) {
            if (set.has(e.pred_id)) out.push({ pred: e.pred_id, succ: succId });
          }
        }
        return out;
      },
      tiesTouching: (ref) => {
        const out: Array<{
          pred: string;
          succ: string;
          type: TrainDepType;
          lag: number;
        }> = [];
        for (const e of graph.edges[ref] ?? []) {
          out.push({
            pred: e.pred_id,
            succ: ref,
            type: e.type,
            lag: Math.round(e.lag_hours / 8),
          });
        }
        for (const [succId, edges] of Object.entries(graph.edges)) {
          if (succId === ref) continue;
          for (const e of edges) {
            if (e.pred_id === ref) {
              out.push({
                pred: ref,
                succ: succId,
                type: e.type,
                lag: Math.round(e.lag_hours / 8),
              });
            }
          }
        }
        return out;
      },
      newId: () => makeId("new"),
      nameOf: (code) => codeNameByKey[code] ?? code,
    };
    applyTrainPlan(buildTrainApplyReview(protoTrain, ctx), graph, new Set());
    return true;
    // depGraphRef ref is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    protoTrain,
    depEditorSection,
    scheduleId,
    effectiveDraft,
    protoSpanOf,
    scopeMembers,
    protoNewActs,
    codeNameByKey,
    applyTrainPlan,
  ]);


  const handleAddTrainDep = React.useCallback(
    (predCarId: string, succCarId: string, type: TrainDepType, lagDays: number) => {
      if (!protoTrain) return;
      if (predCarId !== succCarId && trainWouldCycle(protoTrain.deps, predCarId, succCarId)) {
        notify("Can't add this tie — it would create a loop.", "error");
        return;
      }
      const deps = addTrainDep(protoTrain.deps, predCarId, succCarId, type, lagDays);
      if (deps === protoTrain.deps) return; // duplicate tie: no-op
      pushUndo("Add train tie");
      setProtoTrain((t) => (t ? { ...t, deps } : t));
    },
    // setProtoTrain setter is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [protoTrain, pushUndo],
  );
  const handleUpdateTrainDep = React.useCallback(
    (predCarId: string, succCarId: string, type: TrainDepType, lagDays: number) => {
      if (!protoTrain) return;
      const cur = protoTrain.deps.find(
        (d) => d.predCarId === predCarId && d.succCarId === succCarId,
      );
      if (!cur || (cur.type === type && cur.lagDays === lagDays)) return;
      pushUndo("Edit train tie");
      const deps = updateTrainDep(protoTrain.deps, predCarId, succCarId, type, lagDays);
      setProtoTrain((t) => (t ? { ...t, deps } : t));
    },
    // setProtoTrain setter is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [protoTrain, pushUndo],
  );
  const handleRemoveTrainDep = React.useCallback(
    (predCarId: string, succCarId: string) => {
      if (!protoTrain) return;
      const deps = removeTrainDep(protoTrain.deps, predCarId, succCarId);
      if (deps.length === protoTrain.deps.length) return; // nothing removed
      pushUndo("Remove train tie");
      setProtoTrain((t) => (t ? { ...t, deps } : t));
    },
    // setProtoTrain setter is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [protoTrain, pushUndo],
  );
  const handleUpdateTrainCarDuration = React.useCallback(
    (carId: string, durationDays: number) => {
      if (!protoTrain) return;
      const cur = protoTrain.cars.find((c) => c.carId === carId);
      const d = Math.max(1, Math.round(durationDays) || 1);
      if (!cur || cur.durationDays === d) return;
      pushUndo("Edit train car");
      setProtoTrain(updateTrainCarDuration(protoTrain, carId, d));
    },
    // setProtoTrain setter is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [protoTrain, pushUndo],
  );
  const handleRemoveTrainCars = React.useCallback(
    (carIds: string[]) => {
      if (!protoTrain || carIds.length === 0) return;
      pushUndo(carIds.length > 1 ? `Remove ${carIds.length} cars` : "Remove train car");
      setProtoTrain((t) => {
        if (!t) return t;
        let next = t;
        for (const id of carIds) next = removeTrainCar(next, id);
        return next.cars.length === 0 ? null : next;
      });
    },
    // setProtoTrain setter is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [protoTrain, pushUndo],
  );

  // Per-element day positions: "static" = each element's hand-set startIndex (so
  // editing doesn't move bars); "forward" = the schedule simulation.
  // A train is a RELATIVE sequence — always laid out from day 0 by its durations
  // Train layout follows the Forward-pass toggle. "board" = your hand-placed
  // arrangement (static car.startIndex) — the baseline that never jumps as you
  // edit; "live" = a forward pass over the ties + durations (simulateTrain) —
  // what your logic would actually drive. Toggling back to "board" restores the
  // arrangement you had.
  const trainPos = React.useMemo(() => {
    if (!protoTrain) return new Map<string, DayPos>();
    if (trainSeqMode === "live") return simulateTrain(protoTrain);
    const m = new Map<string, DayPos>();
    for (const car of protoTrain.cars) {
      const s = Math.max(0, car.startIndex);
      m.set(car.carId, { startDay: s, finishDay: s + Math.max(1, car.durationDays) });
    }
    return m;
  }, [protoTrain, trainSeqMode]);

  // "Edit train" reuses the Ties tab scoped to JUST the train: a synthetic graph
  // keyed by carId, with real workday dates from trainPos so the gantt renders.
  const trainGraphBundle = React.useMemo(() => {
    if (!protoTrain) return null;
    return buildTrainGraph(
      protoTrain,
      codeNameByKey,
      effectiveCodeColors,
      trainPos,
      protoCalendar,
    );
  }, [protoTrain, codeNameByKey, effectiveCodeColors, trainPos, protoCalendar]);
  // Committed activity ids mapped to the train — highlighted on the board so you
  // can see every instance of "this train" at once.
  const trainMappedRefs = React.useMemo(() => {
    const ids = new Set<number>();
    mappedRefs(protoTrain).forEach((r) => {
      const n = Number(r);
      if (Number.isFinite(n) && String(n) === r) ids.add(n);
    });
    return ids;
  }, [protoTrain]);

  return {
    handleCreateTrain,
    handleClearTrain,
    trainLevelChips,
    handleRemoveTrainLevel,
    handleAddTrainLevel,
    trainCanAddLevel,
    handleApplyTrain,
    handleAddTrainDep,
    handleUpdateTrainDep,
    handleRemoveTrainDep,
    handleUpdateTrainCarDuration,
    handleRemoveTrainCars,
    trainGraphBundle,
    trainMappedRefs,
  };
}
